// CLI reference — the full command tree with one-line descriptions and role
// requirements. Kept in step with specs/cli.md §5 (the normative tree).

import { IconTerminal2 } from "@tabler/icons-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DocSection, K, P } from "../doc-ui";

interface Cmd {
  cmd: string;
  what: string;
  who?: string;
}

function CmdTable({ rows }: { rows: Cmd[] }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Command</TableHead>
            <TableHead>What it does</TableHead>
            <TableHead>Requires</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.cmd}>
              <TableCell className="font-mono whitespace-nowrap">{r.cmd}</TableCell>
              <TableCell className="text-muted-foreground">{r.what}</TableCell>
              <TableCell className="font-mono text-muted-foreground">{r.who ?? ""}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const SESSION: Cmd[] = [
  { cmd: "cairn login", what: "Sign this machine in (prompts server, email, password, TOTP)" },
  { cmd: "cairn logout", what: "Sign the machine out; all its vaults stop syncing" },
  { cmd: "cairn whoami", what: "Show the signed-in account and server" },
];

const VAULT: Cmd[] = [
  { cmd: "cairn vault list", what: "Vaults you can access; marks locally-linked ones" },
  { cmd: "cairn vault create <name> [--clone <path>]", what: "Create a vault; --clone also clones it locally", who: "member+" },
  { cmd: "cairn vault clone <vault> [path]", what: "Prepare a local folder for a vault (default folder: the vault's name)" },
  { cmd: "cairn vault link <path> [--vault <id>]", what: "Attach an existing folder — to a new vault, or --vault for an existing one" },
  { cmd: "cairn vault unlink <path>", what: "Stop the folder syncing; remote vault untouched" },
  { cmd: "cairn vault members <vault>", what: "List members and roles" },
  { cmd: "cairn vault add <vault> <user> [--role r]", what: "Add an existing user (viewer default; owner allowed)", who: "owner" },
  { cmd: "cairn vault role <vault> <user> <role>", what: "Change a member's role", who: "owner" },
  { cmd: "cairn vault remove <vault> <user>", what: "Remove a member", who: "owner" },
  { cmd: "cairn vault leave <vault>", what: "Remove yourself (not as sole owner)" },
  { cmd: "cairn vault delete <vault> [--yes]", what: "Delete the vault for everyone; confirms first", who: "owner" },
];

const USER: Cmd[] = [
  { cmd: "cairn user invite [--email x]", what: "Mint an account-invite link for a new person", who: "manager+" },
  { cmd: "cairn user invites", what: "List pending invites", who: "manager+" },
  { cmd: "cairn user cancel-invite <id>", what: "Cancel a pending invite", who: "manager+" },
  { cmd: "cairn user regenerate-invite <id>", what: "Rotate an invite's link; the old one dies", who: "manager+" },
  { cmd: "cairn user list", what: "The user directory with roles", who: "member+" },
  { cmd: "cairn user set-role <user> <role>", what: "Set a server role (admin/manager/member/guest)", who: "admin" },
  { cmd: "cairn user disable <user>", what: "Block sign-in and sync", who: "admin" },
  { cmd: "cairn user enable <user>", what: "Restore a disabled account", who: "admin" },
  { cmd: "cairn user reset-password <user>", what: "Print a password-reset link", who: "admin" },
  { cmd: "cairn user totp-clear <user>", what: "Clear a user's two-factor enrollment", who: "admin" },
];

const SERVER: Cmd[] = [
  { cmd: "cairn server stats", what: "Instance stats", who: "admin" },
  { cmd: "cairn server gc", what: "Reclaim space from deleted content", who: "admin" },
  { cmd: "cairn server compaction", what: "Compact document sync history", who: "admin" },
  { cmd: "cairn server quota <vault> <bytes>", what: "Cap a vault's attachment storage", who: "admin" },
];

const UPDATES: Cmd[] = [
  { cmd: "cairn version", what: "CLI + server versions; whether an update is available" },
  { cmd: "cairn update", what: "Self-update the CLI from your server" },
  { cmd: "cairn plugin update [<path>]", what: "Update the plugin in one vault, or every linked vault" },
];

export function CliReferenceDoc() {
  return (
    <>
      <P>
        Commands are grouped by what they touch: <K>vault</K> for vault-scoped work, <K>user</K>{" "}
        for accounts and the roster, <K>server</K> for instance operations. Anywhere a command
        takes <K>&lt;vault&gt;</K> or <K>&lt;user&gt;</K>, a unique name works as well as an id.
        Global flags: <K>--server &lt;url&gt;</K>, <K>--config-dir &lt;path&gt;</K>, <K>--json</K>,{" "}
        <K>-v</K>.
      </P>

      <DocSection icon={IconTerminal2} title="Session">
        <CmdTable rows={SESSION} />
      </DocSection>
      <DocSection icon={IconTerminal2} title="Vaults">
        <CmdTable rows={VAULT} />
      </DocSection>
      <DocSection icon={IconTerminal2} title="Users">
        <CmdTable rows={USER} />
      </DocSection>
      <DocSection icon={IconTerminal2} title="Server">
        <CmdTable rows={SERVER} />
      </DocSection>
      <DocSection icon={IconTerminal2} title="Updates">
        <CmdTable rows={UPDATES} />
      </DocSection>
    </>
  );
}
